import { Injectable, inject } from '@angular/core';

import { CompetitionBatchRequest } from '@cds/query-objects';
import { CompetitionStatistics } from '@cds/statistics/competition';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';

import { StatisticsApi } from './statistics-cds-api.service';
import { StatisticsData } from './statistics.models';

@Injectable({
    providedIn: 'root',
})
export class StatisticsApiService {
    private cdsStatsService = inject(StatisticsApi);

    private readonly logger: SportsRemoteLogger;

    constructor() {
        const loggerFactory = inject(LoggerFactory);

        this.logger = loggerFactory.getLogger('StatisticsApiService');
    }

    getStats(eventId: string, leagueId: number, sportId: number, stageId?: number, groupId?: number): Observable<StatisticsData> {
        const leagueStatistics$ = this.cdsStatsService.getCompetitionStatistics(leagueId, sportId, stageId, groupId);
        const eventStatistics$ = this.cdsStatsService.getFixtureStatistics(eventId, sportId);

        return forkJoin([leagueStatistics$, eventStatistics$]).pipe(
            map(([leagueStatistics, eventStatistics]) => ({ eventStats: eventStatistics, leagueStats: leagueStatistics?.rows })),
            catchError((error: unknown) => {
                this.logError('getStats', error);

                return of({ eventStats: undefined, leagueStats: [] });
            }),
        );
    }

    getCompetitionsStatistics(competitionIds: number[], sportId: number): Observable<CompetitionStatistics[]> {
        return this.cdsStatsService.getCompetitionStatisticsBatch(competitionIds.join(','), sportId).pipe(
            map((value) => value ?? []),
            catchError((error: unknown) => {
                this.logError('getCompetitionsStatistics', error);

                return of([]);
            }),
        );
    }

    getCompetitionsBatchStatistics(competitionBatchRequest: CompetitionBatchRequest): Observable<CompetitionStatistics[]> {
        return this.cdsStatsService.getCompetitionStatisticsBatchV2(competitionBatchRequest).pipe(
            map((value) => value ?? []),
            catchError((error: unknown) => {
                this.logError('competition-batch/v2', error);

                return of([]);
            }),
        );
    }

    private logError(methodName: string, error: unknown): void {
        this.logger.error(error, `Failed to load statistics from ${methodName}`);
    }
}
